import { useMemo } from 'react';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { EmptyState, ErrorState } from '@/pages/Dashboard';
import { getProjects } from '@/services/api';
import {
    getLeadLifetimeCurve,
    getLeadLifetimeDaily,
    getLeadLifetimeFilters,
    getLeadLifetimePages,
    getLeadLifetimeSummary,
} from '@/services/api/lead-lifetime';
import { CurveChart } from './components/CurveChart';
import { DailyChart } from './components/DailyChart';
import { DailyTable } from './components/DailyTable';
import { Filters } from './components/Filters';
import { PagesTable } from './components/PagesTable';
import { RevenueMix } from './components/RevenueMix';
import { MoneyProvider } from './components/currency';
import { SummaryCards } from './components/SummaryCards';
import { fmtLL } from './components/format';
import { LL_QUERY_KEYS, useLeadLifetimeFilters } from './hooks/useLeadLifetimeFilters';

const PERIOD_LABEL: Record<string, string> = {
    today: 'HOJE', yesterday: 'ONTEM', '7d': '7D', this_month: 'MÊS', last_month: 'MÊS ANT.', '60d': '60D', '90d': '90D',
};

const retry = (count: number, err: any) => count < 1 && ![403, 404, 422].includes(err?.response?.status);

export default function LeadLifetime() {
    const filters = useLeadLifetimeFilters();

    const isOwner = useMemo(() => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            return user?.owner === true || user?.owner === 1;
        } catch {
            return false;
        }
    }, []);

    const { data: projects } = useQuery({ queryKey: ['projects'], queryFn: getProjects });
    const { data: options } = useQuery({
        queryKey: ['lead_lifetime_filters', filters.preset, filters.projectId],
        queryFn: () => getLeadLifetimeFilters(filters.preset, filters.projectId),
        staleTime: 5 * 60 * 1000,
    });

    const req = filters.request;
    const summary = useQuery({ queryKey: ['lead_lifetime_summary', req], queryFn: () => getLeadLifetimeSummary(req), retry });
    const curve = useQuery({ queryKey: ['lead_lifetime_curve', req], queryFn: () => getLeadLifetimeCurve(req), retry });
    const daily = useQuery({ queryKey: ['lead_lifetime_daily', req], queryFn: () => getLeadLifetimeDaily(req), retry });
    // Comparativo no fim: sempre todas as páginas visíveis (a selecionada fica destacada).
    const pages = useQuery({
        queryKey: ['lead_lifetime_pages', filters.comparisonRequest],
        queryFn: () => getLeadLifetimePages(filters.comparisonRequest),
        retry,
    });

    const busy = useIsFetching({ predicate: (q) => LL_QUERY_KEYS.includes(String(q.queryKey[0])) }) > 0;
    const periodLabel = PERIOD_LABEL[filters.preset] || filters.preset.toUpperCase();
    const selectedPage = filters.pageId ? options?.pages.find((p) => p.id === filters.pageId) : undefined;
    const s = summary.data;
    const period = s?.period;

    return (
        <MoneyProvider currency={filters.currency} rate={s?.meta?.dolar_rate || 0}>
        <div className="space-y-6 font-mono">
            <div className="border-b border-border pb-4">
                <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">relatórios / vida útil do lead</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-foreground">Vida útil do lead</h1>
                <p className="mt-1 text-xs text-muted-foreground md:text-sm">
                    Quanto custa trazer um lead pelo Messenger, quanto ele rende dia a dia depois de entrar e em que dia se paga
                </p>
            </div>

            <Filters state={filters} projects={projects} isOwner={isOwner} options={options} busy={busy} />

            {filters.pageId && (
                <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-2 text-xs">
                    <span>
                        Analisando só a página <b>{selectedPage?.name || filters.pageId}</b>
                    </span>
                    <button type="button" onClick={() => filters.setPageId(undefined)} className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
                        <X className="h-3.5 w-3.5" /> ver todas
                    </button>
                </div>
            )}

            {summary.isError ? (
                <ErrorState message={(summary.error as any)?.response?.data?.detail || (summary.error as Error)?.message} onRetry={() => summary.refetch()} />
            ) : summary.isLoading || !s ? (
                <div className="flex items-center justify-center rounded-lg border border-border bg-card py-16 text-muted-foreground">
                    <div className="mr-3 h-4 w-4 animate-spin rounded-full border-b-2 border-muted-foreground" />
                    <span className="text-sm">Calculando CAC, LTV e payback…</span>
                </div>
            ) : s.empty || period?.empty ? (
                <EmptyState
                    title="Sem dados no período"
                    description={
                        period?.empty
                            ? `Os dados começam em ${fmtLL.date(period.floor)}. Escolha um período a partir dessa data.`
                            : (s.reason || 'Nenhum domínio com receita de messageria neste recorte.')
                    }
                />
            ) : (
                <>
                    <SummaryCards summary={s} basis={filters.basis} periodLabel={periodLabel} />

                    {s.no_cost && s.no_cost.days.length > 0 && (
                        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-2 text-xs text-amber-600 dark:text-amber-400">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>
                                {s.no_cost.days.length} dia(s) do recorte não têm nenhum gasto da Meta
                                {s.no_cost.first_cost_date ? ` (o custo desse recorte começa em ${fmtLL.date(s.no_cost.first_cost_date)})` : ''} —
                                os {fmtLL.num(s.no_cost.leads)} leads que entraram neles rendem receita, mas ficam fora do CAC, da curva e do payback.
                            </span>
                        </div>
                    )}

                    {filters.basis === 'lead' && s.lead_spike_days.length > 0 && (
                        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-2 text-xs text-amber-600 dark:text-amber-400">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>
                                Em {s.lead_spike_days.map(fmtLL.date).join(', ')} os leads novos passaram muito das conversas da Meta — sinal de
                                importação em massa, que infla a contagem e baixa o CAC por lead desses dias.
                            </span>
                        </div>
                    )}

                    <RevenueMix channels={s.revenue_mix} />

                    {curve.data ? <CurveChart curve={curve.data} basis={filters.basis} /> : null}
                    {daily.data ? <DailyChart days={daily.data.days} /> : null}
                    {daily.data ? <DailyTable days={daily.data.days} basis={filters.basis} /> : null}
                    {pages.data && pages.data.pages.length > 0 ? (
                        <PagesTable pages={pages.data.pages} basis={filters.basis} selectedPageId={filters.pageId} onSelect={filters.setPageId} />
                    ) : null}

                    <p className="text-right text-[10px] text-muted-foreground">
                        {period ? `coortes de ${fmtLL.date(period.from)} a ${fmtLL.date(period.to)} · ` : ''}
                        dados desde {period ? fmtLL.date(period.floor) : '01/09/2026'} · valores líquidos (revshare, imposto e spread)
                        {s.meta ? ` · dólar ${s.meta.dolar_rate.toFixed(2).replace('.', ',')}${filters.currency === 'USD' ? ' (valores convertidos por ela)' : ''}` : ''}
                    </p>
                </>
            )}
        </div>
        </MoneyProvider>
    );
}
